import React from 'react';
import { ShoppingCart, CheckCircle, Clock, Truck, XCircle, Eye, Edit } from 'lucide-react';

// Order list: stat cards (click to filter), table, row actions. All state
// and handlers come from useOrders — this component is presentation only.
const OrdersTab = ({ orders: ordersHook }) => {
  const {
    orders, orderFilter, setOrderFilter, loadingOrders,
    handleViewOrder, handleUpdateOrderStatus, getStatusColor,
  } = ordersHook;

  const filteredOrders = orderFilter === 'all' ? orders : orders.filter((o) => o.status === orderFilter);

  return (
    <div className="p-6">
      {/* Orders header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Order Management</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">{orders.length} orders</span>
        </div>
      </div>

      {/* Order Statistics (click a card to filter) */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
            </div>
          </div>
        </div>

        {[
          { key: 'CONFIRMED', label: 'Confirmed', icon: CheckCircle },
          { key: 'PROCESSING', label: 'Processing', icon: Clock },
          { key: 'SHIPPED', label: 'Shipped', icon: Truck },
          { key: 'DELIVERED', label: 'Delivered', icon: CheckCircle },
          { key: 'CANCELLED', label: 'Cancelled', icon: XCircle },
        ].map((card) => {
          const count = orders.filter((o) => o.status === card.key).length;
          const active = orderFilter === card.key;
          const Icon = card.icon;
          return (
            <button
              key={card.key}
              onClick={() => setOrderFilter(active ? 'all' : card.key)}
              className={`bg-white p-6 rounded-lg shadow-md text-left ${active ? 'ring-2 ring-[#d44100]' : 'hover:shadow-lg'}`}
            >
              <div className="flex items-center">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Icon className="h-6 w-6 text-gray-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loadingOrders ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#d44100]"></div>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No orders</h3>
            <p className="mt-1 text-sm text-gray-500">Orders will appear here when customers place them.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fulfillment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">#{order.orderNumber}</div>
                        <div className="text-sm text-gray-500">{order.orderItems?.length || 0} items</div>
                        {order.orderItems && order.orderItems.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1">
                            {order.orderItems.slice(0, 2).map((item, index) => (
                              <span key={index}>
                                {item.product?.name}
                                {item.selectedColor && <span className="ml-1">({item.selectedColor})</span>}
                                {index < Math.min(order.orderItems.length, 2) - 1 && ', '}
                              </span>
                            ))}
                            {order.orderItems.length > 2 && ` +${order.orderItems.length - 2} more`}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{order.customer?.fullName || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{order.customer?.email || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">KSh {order.total?.toLocaleString() || '0'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <p className="text-xs">
                        <span className="font-medium text-gray-600">Delivery:</span>{' '}
                        {order.orderAssignment?.deliveryPerson?.fullName || <span className="text-gray-400">Awaiting assignment</span>}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button onClick={() => handleViewOrder(order.id)} className="text-orange-600 hover:text-orange-700 transition-colors" title="View Order Details">
                          <Eye className="h-4 w-4" />
                        </button>
                        {!['DELIVERED', 'CANCELLED', 'REFUNDED'].includes(order.status) && (
                          <button
                            onClick={() => handleUpdateOrderStatus(order.id, 'PROCESSING')}
                            className="text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Mark as Processing"
                            disabled={order.status === 'PROCESSING' || ['DELIVERED', 'CANCELLED', 'REFUNDED'].includes(order.status)}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersTab;
